import type { ScoContentPermissions } from '@src/graphql-types'

import { ADVISOR_TEAM_PRODUCT_ID, STARTUPS_PRO_PRODUCT_ID, type PaywallIntentId } from './intent'

export type ContentPermissionKey = Exclude<keyof ScoContentPermissions, '__typename' | 'id'>

/**
 * Permissions the Advisor Team pack grants and the Startups Pro pack does not
 * (startups3 `config/productPermissions/advisor.js` vs `tools.js`, checked 2026-09-24).
 */
export const TEAM_ONLY_PERMISSIONS: readonly ContentPermissionKey[] = [
  'canAccessAdvisorTeam',
  'canAccessFGAdvisor',
  'canAccessFGOfficeHours',
  'canAccessFGPremiumEvents',
]

/**
 * Permissions the Startups Pro pack (`scom-accelerator-tools`) grants. Advisor Team spreads the
 * same pack, so these are included there too.
 */
export const PRO_PERMISSIONS: readonly ContentPermissionKey[] = [
  'canAccessChat',
  'canAccessCourses',
  'canAccessFGAccountManager',
  'canAccessFGBasicEvents',
  'canAccessFGDashboard',
  'canAccessFGDiscussions',
  'canAccessFGInvestorContacts',
  'canAccessFGMemberDirectory',
  'canAccessFGRequests',
  'canAccessFGWorkshops',
  'canAccessFGZirtual',
  'canAccessGetCustomers',
  'canAccessInvestorMatching',
  'canAccessMemberMatching',
  'canAccessPitchDeckAuditor',
  'canAccessPodcast',
  'canAccessPremiumBenefits',
  'canAccessVideos',
  'canActivateBizplan',
  'canActivateFundable',
  'canActivateLaunchrock',
  // Any paid plan grants it (startups3 canPostPublicComments); Pro is the cheapest one to sell.
  'canPostPublicComments',
]

/** The founder moment a permission usually guards. Call sites pass a sharper intent when they have one. */
const DEFAULT_INTENTS: Partial<Record<ContentPermissionKey, PaywallIntentId>> = {
  canAccessAdvisorTeam: 'leadership-message',
  canAccessCourses: 'content-lock',
  canAccessFGBasicEvents: 'event-rsvp',
  canAccessFGInvestorContacts: 'investor-search',
  canAccessFGOfficeHours: 'office-hours',
  canAccessFGPremiumEvents: 'premium-event',
  canAccessMemberMatching: 'member-match',
  canAccessPitchDeckAuditor: 'deck-slide-lock',
  canAccessPodcast: 'content-lock',
  canAccessPremiumBenefits: 'perk-unlock',
  canAccessVideos: 'content-lock',
  canPostPublicComments: 'discussion-join',
}

export type PermissionConversion = {
  permission: ContentPermissionKey
  productId: string
  intent: PaywallIntentId
}

/**
 * The cheapest ladder plan that grants a permission, and the intent to open with. Returns null
 * for permissions neither Startups Pro nor Advisor Team grants (funding accelerator, pitch-deck
 * service, ...): those gates must name their product explicitly rather than sell a plan that
 * would not unlock them.
 */
export function conversionForPermission(
  permission: ContentPermissionKey | null | undefined
): PermissionConversion | null {
  if (!permission) {
    return null
  }
  const productId = TEAM_ONLY_PERMISSIONS.includes(permission)
    ? ADVISOR_TEAM_PRODUCT_ID
    : PRO_PERMISSIONS.includes(permission)
      ? STARTUPS_PRO_PRODUCT_ID
      : null
  if (!productId) {
    return null
  }
  return { permission, productId, intent: DEFAULT_INTENTS[permission] ?? 'default' }
}
